import { AlertCircle, BarChart3, CalendarRange, LoaderCircle } from "lucide-react";
import { useEffect, useId, useRef, useState } from "react";
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  LabelList,
  Legend,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";
import { useObraId } from "@/app/useObraId";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { ExportButton } from "@/components/ui/ExportButton";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { type InformeLotos, type RangoFechas, useInformeLotos } from "@/lib/informes";

// Etiqueta legible de cada estado del ciclo de vida BINARIO del LOTO (#241), para ejes y
// tarjetas. Se exporta para que un test fije el contrato: nombrar estados retirados dejaría
// barras huérfanas en la gráfica.
export const ESTADO_LABEL: Record<string, string> = {
  ABIERTO: "Abierto",
  CERRADO: "Cerrado",
};

// Color de cada estado, en línea con los badges del panel (navy/pizarra): así el informe y el
// resto de la app leen como un solo sistema. Orden fijo (no se recicla por rango).
export const ESTADO_COLOR: Record<string, string> = {
  ABIERTO: "#3b4759", // navy-500
  CERRADO: "#94a3b8", // slate-400
};

// Hues de magnitud: teal de marca para el total/equipos, navy para los cerrados (2ª serie del
// gráfico por mes). Un solo tono por medida, nada de arcoíris.
const COLOR_TOTAL = "#009578"; // primary-500
const COLOR_CERRADOS = "#3b4759"; // navy-500

/**
 * InformesPage muestra los indicadores estadísticos de los LOTOs de la obra activa (#215):
 * tarjetas resumen (total, cerrados, duración media, candados activos) y tres gráficas —por
 * estado, aperturas por mes (con cuántas se cerraron) y ranking de equipos—. Un filtro de rango
 * de fechas (sobre la apertura) acota todo el informe. Es una vista de solo lectura y solo-online
 * (no usa la caché offline): sin red, se muestra el error estándar de la consulta. Los datos los
 * calcula el backend en SQL; la obra la fija la ruta (`/obras/$obraId/informes`).
 */
export function InformesPage() {
  const obraId = useObraId();
  const [rango, setRango] = useState<RangoFechas>({});
  const informe = useInformeLotos(obraId, rango);

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <BarChart3 className="h-6 w-6 text-primary-600" aria-hidden="true" />
          <h1 className="font-bold text-2xl text-slate-900 tracking-tight">Informes de LOTOs</h1>
        </div>
        <div className="flex flex-wrap items-center justify-end gap-2">
          {/* Informe GENERAL de la obra (#249 D1): un libro con una hoja por entidad, sin
              filtros. No es el informe de esta pantalla (que sí acota por rango): es la
              documentación del estado de la obra para entregar. */}
          <ExportButton tipo="reporte" obraId={obraId} etiqueta="Descargar informe de obra" />
          <FiltroRango rango={rango} onChange={setRango} />
        </div>
      </div>

      {informe.isError ? (
        <Alert variant="destructive">
          <AlertCircle className="mt-0.5 h-5 w-5 shrink-0" aria-hidden="true" />
          <span>No se pudo cargar el informe. Comprueba tu conexión e inténtalo de nuevo.</span>
        </Alert>
      ) : informe.isPending ? (
        <div className="flex items-center justify-center gap-2 py-16 text-slate-500">
          <LoaderCircle className="h-6 w-6 animate-spin" aria-hidden="true" />
          Cargando…
        </div>
      ) : (
        <InformeContenido informe={informe.data} conRango={!!rango.desde || !!rango.hasta} />
      )}
    </div>
  );
}

// InformeContenido pinta las tarjetas y gráficas con los datos ya cargados, o un estado vacío.
function InformeContenido({ informe, conRango }: { informe: InformeLotos; conRango: boolean }) {
  if (informe.total === 0) {
    return (
      <div className="rounded-2xl border border-slate-200 border-dashed bg-white px-4 py-16 text-center text-slate-500">
        {conRango
          ? "No hay LOTOs en el rango de fechas seleccionado."
          : "Esta obra todavía no tiene LOTOs registrados."}
      </div>
    );
  }

  const cerrados = informe.por_estado.find((e) => e.estado === "CERRADO")?.total ?? 0;
  const datosEstado = informe.por_estado.map((e) => ({
    estado: e.estado,
    nombre: ESTADO_LABEL[e.estado] ?? e.estado,
    total: e.total,
  }));
  const datosMes = informe.por_mes.map((m) => ({
    mes: formatMes(m.mes),
    total: m.total,
    cerrados: m.cerrados,
  }));
  const datosEquipo = informe.por_equipo.map((eq) => ({
    nombre: eq.equipo || eq.codigo_equipo || `#${eq.id_equipo}`,
    total: eq.total,
  }));

  return (
    <div className="flex flex-col gap-6">
      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4">
        <ResumenCard label="LOTOs en total" value={String(informe.total)} />
        <ResumenCard label="LOTOs cerrados" value={String(cerrados)} />
        <ResumenCard label="Duración media" value={formatDuracion(informe.duracion_media_horas)} />
        <ResumenCard label="Candados activos" value={String(informe.candados_activos)} />
      </div>

      <div className="grid grid-cols-1 gap-6 xl:grid-cols-2">
        <GraficaCard titulo="LOTOs por estado">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={datosEstado} margin={{ top: 16, right: 12, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="nombre" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="total" radius={[4, 4, 0, 0]} name="LOTOs">
                {datosEstado.map((d) => (
                  <Cell key={d.estado} fill={ESTADO_COLOR[d.estado] ?? COLOR_TOTAL} />
                ))}
                <LabelList dataKey="total" position="top" fontSize={12} fill="#475569" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </GraficaCard>

        <GraficaCard titulo="Aperturas por mes">
          <ResponsiveContainer width="100%" height={280}>
            <BarChart data={datosMes} margin={{ top: 16, right: 12, bottom: 4, left: -8 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
              <XAxis dataKey="mes" tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Bar dataKey="total" name="Aperturas" fill={COLOR_TOTAL} radius={[4, 4, 0, 0]} />
              <Bar dataKey="cerrados" name="Cerrados" fill={COLOR_CERRADOS} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </GraficaCard>

        <GraficaCard titulo="LOTOs por equipo" className="xl:col-span-2">
          <ResponsiveContainer width="100%" height={Math.max(160, datosEquipo.length * 40 + 40)}>
            <BarChart
              data={datosEquipo}
              layout="vertical"
              margin={{ top: 4, right: 32, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" horizontal={false} />
              <XAxis type="number" allowDecimals={false} tick={{ fontSize: 12 }} stroke="#94a3b8" />
              <YAxis
                type="category"
                dataKey="nombre"
                width={140}
                tick={{ fontSize: 12 }}
                stroke="#94a3b8"
              />
              <Tooltip cursor={{ fill: "#f1f5f9" }} />
              <Bar dataKey="total" name="LOTOs" fill={COLOR_TOTAL} radius={[0, 4, 4, 0]}>
                <LabelList dataKey="total" position="right" fontSize={12} fill="#475569" />
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </GraficaCard>
      </div>
    </div>
  );
}

// FiltroRango replica el popover de rango de fechas de la exportación (#214): dos inputs de fecha
// apilados que acotan el informe por fecha de apertura; se cierra al pulsar fuera o con Escape.
function FiltroRango({
  rango,
  onChange,
}: {
  rango: RangoFechas;
  onChange: (r: RangoFechas) => void;
}) {
  const [abierto, setAbierto] = useState(false);
  const contenedorRef = useRef<HTMLDivElement>(null);
  const desdeId = useId();
  const hastaId = useId();

  useEffect(() => {
    if (!abierto) {
      return;
    }
    function onPointerDown(e: MouseEvent) {
      if (contenedorRef.current && !contenedorRef.current.contains(e.target as Node)) {
        setAbierto(false);
      }
    }
    function onKeyDown(e: KeyboardEvent) {
      if (e.key === "Escape") {
        setAbierto(false);
      }
    }
    document.addEventListener("mousedown", onPointerDown);
    document.addEventListener("keydown", onKeyDown);
    return () => {
      document.removeEventListener("mousedown", onPointerDown);
      document.removeEventListener("keydown", onKeyDown);
    };
  }, [abierto]);

  const desde = rango.desde ?? "";
  const hasta = rango.hasta ?? "";
  const hayRango = desde !== "" || hasta !== "";

  return (
    <div ref={contenedorRef} className="relative">
      <Button
        variant="outline"
        onClick={() => setAbierto((v) => !v)}
        aria-expanded={abierto}
        className={hayRango ? "border-primary-500 text-primary-700" : undefined}
      >
        <CalendarRange className="h-5 w-5" aria-hidden="true" />
        <span>{hayRango ? "Rango activo" : "Filtrar por fechas"}</span>
      </Button>

      {abierto ? (
        <div className="absolute top-full right-0 z-20 mt-2 w-72 rounded-xl border border-slate-200 bg-white p-4 shadow-lg">
          <p className="mb-3 font-semibold text-slate-900 text-sm">Rango de apertura</p>
          <div className="flex flex-col gap-3">
            <div className="flex flex-col gap-1">
              <Label htmlFor={desdeId}>Desde</Label>
              <Input
                id={desdeId}
                type="date"
                value={desde}
                max={hasta === "" ? undefined : hasta}
                onChange={(e) => onChange({ ...rango, desde: e.target.value || undefined })}
              />
            </div>
            <div className="flex flex-col gap-1">
              <Label htmlFor={hastaId}>Hasta</Label>
              <Input
                id={hastaId}
                type="date"
                value={hasta}
                min={desde === "" ? undefined : desde}
                onChange={(e) => onChange({ ...rango, hasta: e.target.value || undefined })}
              />
            </div>
            {hayRango ? (
              <button
                type="button"
                onClick={() => onChange({})}
                className="self-start text-primary-700 text-sm hover:underline"
              >
                Limpiar rango
              </button>
            ) : null}
          </div>
        </div>
      ) : null}
    </div>
  );
}

// ResumenCard muestra un indicador del informe: valor grande + etiqueta.
function ResumenCard({ label, value }: { label: string; value: string }) {
  return (
    <div className="flex flex-col gap-1 rounded-2xl border border-slate-200 bg-white p-4 shadow-sm">
      <span className="font-bold text-3xl text-slate-900 tabular-nums tracking-tight">{value}</span>
      <span className="font-medium text-slate-500 text-sm">{label}</span>
    </div>
  );
}

// GraficaCard envuelve una gráfica en una tarjeta con su título.
function GraficaCard({
  titulo,
  className,
  children,
}: {
  titulo: string;
  className?: string;
  children: React.ReactNode;
}) {
  return (
    <Card className={className}>
      <CardHeader>
        <CardTitle className="text-lg">{titulo}</CardTitle>
      </CardHeader>
      <CardContent>{children}</CardContent>
    </Card>
  );
}

// formatMes convierte "2026-07" a "jul 2026" (es-ES) para el eje temporal; deja el crudo si no
// parsea.
function formatMes(mes: string): string {
  const [anio, m] = mes.split("-").map(Number);
  if (!anio || !m) {
    return mes;
  }
  const fecha = new Date(Date.UTC(anio, m - 1, 1));
  return fecha.toLocaleDateString("es-ES", { month: "short", year: "numeric", timeZone: "UTC" });
}

// formatDuracion formatea las horas medias como "Nd Nh" (o "Nh"); "—" si no hay LOTOs cerrados.
function formatDuracion(horas: number | null): string {
  if (horas == null) {
    return "—";
  }
  const total = Math.round(horas);
  const dias = Math.floor(total / 24);
  const resto = total % 24;
  if (dias > 0) {
    return `${dias}d ${resto}h`;
  }
  return `${resto}h`;
}
